import { MembershipProfileCurrency } from '@models/membership/MembershipProfileCurrency'
import { membershipProfileCurrencySchema } from '@models/schema'
import { createSelector, createSlice } from '@reduxjs/toolkit'
import {
  createMembershipProfileCurrency as createMembershipProfileCurrencyAPI,
  deleteMembershipProfileCurrency as deleteMembershipProfileCurrencyAPI,
  fetchMembershipProfileCurrencies,
} from '@services/membership/MembershipProfileCurrencyService'
import { AppDispatch, RootState } from '@store/store'
import {
  baseReducers,
  getIdOrModelId,
  SBAPI,
  SBSelectRaw,
  SBState,
} from '@supplement-bacon/react-toolkit/utils'
import { initialQuery, TableParams } from '@supplement-bacon/sb-table-filters'

const initialState: SBState<MembershipProfileCurrency> = {
  isLoading: false,
  error: null,
  items: {},
  ids: [],
  selectedId: undefined,
  query: initialQuery,
}

const slice = createSlice({
  name: 'membershipProfileCurrency',
  initialState,
  reducers: baseReducers,
})

// Reducer
export default slice.reducer
export const {
  getItemsSuccess: getMembershipProfileCurrenciesSuccess,
  setQuery: setMembershipProfileCurrencyQuery,
  reset: resetMembershipProfileCurrencyState,
  resetQueryAndIds: resetMembershipProfileCurrencyQueryAndIds,
} = slice.actions

/**
 * Selectors
 */

const selectRawItems: SBSelectRaw<
  RootState,
  { [key: number]: MembershipProfileCurrency }
> = (state: RootState) => state[slice.name].items
const selectRawIds: SBSelectRaw<RootState, number[]> = (state: RootState) =>
  state[slice.name].ids
const selectRawSelectedId: SBSelectRaw<RootState, number | undefined> = (
  state: RootState
) => state[slice.name].selectedId

export const selectMembershipProfileCurrencies = () =>
  createSelector(
    [selectRawItems, selectRawIds],
    (items, ids) => ids.map((id) => items[id]).filter((i) => i)
    // Filter allow to return only non-null elements
  )
export const selectSelectedMembershipProfileCurrency = () =>
  createSelector([selectRawItems, selectRawSelectedId], (items, id) =>
    id !== undefined ? items[id] : undefined
  )
export const selectMembershipProfileCurrencyById = (id: number) =>
  createSelector([selectRawItems], (items) =>
    items.hasOwnProperty(id) ? items[id] : undefined
  )
export const selectMembershipProfileCurrenciesByIds = (ids: number[]) =>
  createSelector([selectRawItems], (items) =>
    ids.filter((id) => items.hasOwnProperty(id)).map((id) => items[id])
  )

/**
 * Actions
 */

export const setSelectedMembershipProfileCurrency =
  (currency: MembershipProfileCurrency | number) =>
  async (dispatch: AppDispatch) =>
    dispatch(
      slice.actions.setSelectedId(
        getIdOrModelId<MembershipProfileCurrency>(currency)
      )
    )

export const getMembershipProfileCurrencies = (params: TableParams) =>
  SBAPI.FetchPaginatedDispatched(
    fetchMembershipProfileCurrencies(params),
    [membershipProfileCurrencySchema],
    slice.actions
  )

export const createMembershipProfileCurrency = (
  currency: MembershipProfileCurrency
) =>
  SBAPI.CreateDispatched(
    createMembershipProfileCurrencyAPI(currency),
    membershipProfileCurrencySchema,
    slice.actions
  )

export const deleteMembershipProfileCurrency = (
  currency: MembershipProfileCurrency
) =>
  SBAPI.DestroyDispatched<MembershipProfileCurrency>(
    deleteMembershipProfileCurrencyAPI(currency),
    currency,
    slice.actions
  )
